/**
 * TaskDetail — Phase 7 Sub-iter 7C.
 *
 * Full-page replacement for the legacy `TaskDetailModal` that lived
 * inside Tasks.jsx.  Routes at `/tasks/:id` (registered in App.js).
 *
 * Tabs:
 *   1. Overview  — title editor / status / instruction / attachments / recurrence
 *   2. Activity  — status timeline + notes thread
 *   3. Transfer  — re-assign to another user with a handover note
 *
 * Trust Contract:
 *   • Rejection prompt replaced with inline 2-step form (no window.prompt).
 *   • Error alerts replaced with toasts (no window.alert).
 *   • No `<Modal>` — the whole flow is the page itself (Rule 2).
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowRight, Pencil, Save, XCircle, X, Clock, Send,
  Image as ImageIcon, Mic, Video as VideoIcon, FileText,
  AlertOctagon, Circle, Trash2,
} from "lucide-react";
import api from "../../lib/api";
import { uploadUrl } from "../../lib/uploads";
import BlurTextarea from "../../components/BlurTextarea";
import { AddToCalendarButton } from "../../lib/calendarExport";
import FromChatPill from "../../components/chat/FromChatPill";
import { toast } from "../../components/ui/sonner";
import { EntityShell } from "../../components/shell";
import HistoryBlock from "../../components/shell/HistoryBlock";
import DictTextarea from "../../components/DictTextarea";

const PRIORITIES = [
  { v: "urgent", label: "Urgent", color: "#F43F5E", icon: AlertOctagon },
  { v: "high",   label: "High",   color: "#F59E0B", icon: AlertOctagon },
  { v: "normal", label: "Normal", color: "#1FB6C1", icon: Circle },
  { v: "low",    label: "Low",    color: "#647087", icon: Circle },
];

const STATUS_TONE = {
  todo: "slate",
  in_progress: "amber",
  completed: "green",
  cancelled: "slate",
  rejected: "red",
};

function AttachmentTile({ att }) {
  const url = uploadUrl(att.id);
  const Icon =
    att.kind === "image" ? ImageIcon : att.kind === "audio" ? Mic : att.kind === "video" ? VideoIcon : FileText;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="border border-white/10 rounded-md p-2 bg-white/[0.02] flex items-center gap-2 group relative"
    >
      {att.kind === "image" ? (
        <img src={url} alt="" className="w-12 h-12 object-cover rounded" />
      ) : att.kind === "video" ? (
        <video src={url} className="w-12 h-12 rounded bg-black" />
      ) : (
        <div className="w-12 h-12 rounded bg-white/5 border border-white/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#a0aabf]" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium truncate">{att.filename}</div>
        <div className="text-[10px] text-[#647087] uppercase">{att.kind}</div>
      </div>
    </a>
  );
}

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [statusLog, setStatusLog] = useState([]);
  const [tab, setTab] = useState("overview");

  // Inline edit state
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);

  // Rejection — Trust Contract Rule 2: inline 2-step
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // Transfer
  const [transferTo, setTransferTo] = useState("");
  const [transferNote, setTransferNote] = useState("");

  // Notes composer
  const [noteDraft, setNoteDraft] = useState("");

  const refresh = useCallback(async () => {
    try {
      const list = await api.get("/tasks");
      const items = Array.isArray(list.data) ? list.data : (list.data?.items || []);
      const t = items.find((x) => x.id === id);
      if (!t) {
        toast.error("Task not found.");
        navigate("/tasks");
        return;
      }
      setTask(t);
      setTitleDraft(t.title || "");
      const log = await api.get(`/task-status-log?task_id=${id}`).catch(() => ({ data: [] }));
      setStatusLog(log.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load task.");
    }
  }, [id, navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  // Load users for transfer
  useEffect(() => {
    api.get("/users")
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : (data?.items || []);
        setUsers(items.filter((u) => u.role !== "client" && u.is_active !== false));
      })
      .catch(() => setUsers([]));
  }, []);

  const setStatus = async (status) => {
    try {
      await api.patch(`/tasks/${id}/status`, { status });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change status.");
    }
  };

  // Save the whole task (used by inline title + instruction edits)
  const saveTask = async (patch) => {
    setSavingMeta(true);
    try {
      const merged = { ...task, ...patch };
      const payload = {
        title: merged.title,
        description: merged.description || "",
        assigned_to_ids: merged.assigned_to_ids || [],
        priority: merged.priority || "normal",
        status: merged.status || "todo",
        due_date: merged.due_date || null,
        attachments: merged.attachments || [],
        linked_customer_id: merged.linked_customer_id || null,
        linked_jobcard_id: merged.linked_jobcard_id || null,
        external_ref: merged.external_ref || null,
        source_type: merged.source_type || null,
        source_id: merged.source_id || null,
        source_chat_thread_id: merged.source_chat_thread_id || null,
        source_chat_message_id: merged.source_chat_message_id || null,
        recurring: !!merged.recurring,
        recurrence_type: merged.recurrence_type || null,
        recurrence_day_of_week: merged.recurrence_day_of_week ?? null,
        recurrence_day_of_month: merged.recurrence_day_of_month ?? null,
        recurrence_date: merged.recurrence_date || null,
      };
      await api.put(`/tasks/${id}`, payload);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save task.");
    } finally {
      setSavingMeta(false);
    }
  };

  const transfer = async () => {
    if (!transferTo) return;
    try {
      await api.post(`/tasks/${id}/transfer`, {
        to_user_id: transferTo,
        note: transferNote || `Transferred to ${users.find((u) => u.id === transferTo)?.name}`,
      });
      toast.success("Task transferred");
      setTransferTo("");
      setTransferNote("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed.");
    }
  };

  const addNote = async () => {
    if (!noteDraft.trim()) return;
    try {
      await api.post(`/tasks/${id}/notes`, { text: noteDraft });
      setNoteDraft("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add note.");
    }
  };

  const confirmReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please give a reason — it's recorded against the task.");
      return;
    }
    try {
      await api.post(`/tasks/${id}/reject`, { reason: rejectReason.trim() });
      toast.success("Task rejected");
      navigate("/tasks");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reject the task.");
    }
  };

  if (!task) {
    return <div className="text-[#647087] p-6">Loading task…</div>;
  }

  const prio = PRIORITIES.find((p) => p.v === task.priority) || PRIORITIES[2];
  const canReject = task.status !== "completed" && task.status !== "rejected";

  const headerActions = (
    <>
      <AddToCalendarButton kind="task" id={task.id} compact />
      {task?.source_chat_thread_id && (
        <FromChatPill
          threadId={task.source_chat_thread_id}
          messageId={task.source_chat_message_id}
        />
      )}
      {canReject && !rejecting && (
        <button
          onClick={() => setRejecting(true)}
          className="hv-btn hv-btn-ghost text-xs text-rose-300"
          data-testid="task-reject-btn"
          title="Reject this task (requires a reason)"
        >
          <X className="w-3 h-3" /> Reject
        </button>
      )}
    </>
  );

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "activity", label: "Activity", count: (task.notes || []).length || undefined },
    { key: "transfer", label: "Transfer" },
  ];

  return (
    <EntityShell
      kindLabel="Task"
      title={task.title}
      status={{ label: (task.status || "todo").replaceAll("_", " "), tone: STATUS_TONE[task.status] || "slate" }}
      backTo="/tasks"
      actions={headerActions}
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
    >
      <div className="space-y-4" data-testid="task-detail">
        {/* Inline rejection panel (Trust Contract Rule 2) */}
        {rejecting && (
          <div className="hv-card border-rose-500/40 bg-rose-500/5 p-4 space-y-2" data-testid="task-reject-panel">
            <div className="text-xs uppercase tracking-wider text-rose-300 font-bold">Reject task — reason</div>
            <DictTextarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Why are you rejecting this task? (required, not editable later)"
              className="hv-input min-h-[80px] text-sm"
              data-testid="task-reject-reason"
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setRejecting(false); setRejectReason(""); }}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="task-reject-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmReject}
                disabled={!rejectReason.trim()}
                className="hv-btn hv-btn-primary bg-rose-500 hover:bg-rose-400 text-xs disabled:opacity-50"
                data-testid="task-reject-confirm"
              >
                <Trash2 className="w-3 h-3" /> Confirm reject
              </button>
            </div>
          </div>
        )}

        {tab === "overview" && (
          <>
            <div className="hv-card p-4 space-y-3" data-testid="task-overview">
              <div data-testid="task-title-editor">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1 flex items-center justify-between">
                  <span>Heading</span>
                  {!editingTitle && (
                    <button
                      type="button"
                      onClick={() => setEditingTitle(true)}
                      className="text-[#1fb6c1] hover:underline inline-flex items-center gap-1"
                      data-testid="task-title-edit-btn"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                  )}
                </div>
                {editingTitle ? (
                  <div className="flex items-center gap-2">
                    <input
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      className="hv-input flex-1 font-display text-lg font-bold"
                      autoFocus
                      data-testid="task-title-input"
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        if (titleDraft.trim() && titleDraft !== task.title) {
                          await saveTask({ title: titleDraft.trim() });
                        }
                        setEditingTitle(false);
                      }}
                      disabled={savingMeta}
                      className="hv-btn hv-btn-primary px-2 py-1 text-xs"
                      data-testid="task-title-save-btn"
                    >
                      <Save className="w-3 h-3" /> Save
                    </button>
                    <button
                      type="button"
                      onClick={() => { setTitleDraft(task.title); setEditingTitle(false); }}
                      className="hv-btn hv-btn-ghost px-2 py-1 text-xs"
                    >
                      <XCircle className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <h2 className="font-display text-xl font-bold tracking-tight">{task.title}</h2>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold"
                  style={{ background: `${prio.color}1a`, color: prio.color, border: `1px solid ${prio.color}55` }}
                >
                  {prio.label}
                </span>
                <select
                  value={task.status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="hv-input py-1 text-xs w-40"
                  disabled={task.status === "rejected"}
                  data-testid="task-status-select"
                >
                  <option value="todo">To do</option>
                  <option value="in_progress">In progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  {task.status === "rejected" && <option value="rejected">Rejected</option>}
                </select>
              </div>

              {task.status === "rejected" && task.rejection_reason && (
                <div className="border border-rose-500/30 bg-rose-500/5 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-wider text-rose-300 mb-1">
                    Rejected by {task.rejected_by_name}
                  </div>
                  <div className="text-sm whitespace-pre-wrap text-[#dde3ec]">{task.rejection_reason}</div>
                </div>
              )}

              <div data-testid="task-instruction-editor">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">Instruction</div>
                <BlurTextarea
                  value={task.description || ""}
                  onSave={(v) => saveTask({ description: v })}
                  testid="task-instruction-input"
                  placeholder="Add a long-form instruction for whoever picks this up — what to do, where, edge-cases…"
                  className="hv-input mt-1 min-h-[100px] text-sm whitespace-pre-wrap"
                />
              </div>

              {(task.attachments || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#647087] mb-2">Attachments</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {task.attachments.map((a) => (
                      <AttachmentTile key={a.id} att={a} />
                    ))}
                  </div>
                </div>
              )}

              {task.recurring && (
                <div className="border border-violet-500/30 bg-violet-500/5 rounded-md p-3" data-testid="task-recurrence-info">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-violet-300 font-bold mb-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Recurring task
                  </div>
                  <div className="text-sm text-[#dde3ec]">
                    {task.recurrence_type === "daily" && "Repeats every day"}
                    {task.recurrence_type === "weekly" && task.recurrence_day_of_week != null &&
                      `Repeats every week on ${["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][task.recurrence_day_of_week]}`}
                    {task.recurrence_type === "monthly" && task.recurrence_day_of_month &&
                      `Repeats every month on day ${task.recurrence_day_of_month}`}
                    {task.recurrence_type === "once" && task.recurrence_date &&
                      `Single instance on ${new Date(task.recurrence_date).toLocaleDateString('en-GB')}`}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {tab === "activity" && (
          <>
            {statusLog.length > 0 && (
              <div className="hv-card p-4" data-testid="task-status-timeline">
                <div className="text-xs uppercase tracking-wider text-[#647087] mb-2 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Status timeline
                </div>
                <ol className="space-y-1.5">
                  {statusLog.map((ev) => (
                    <li key={ev.id} className="text-xs text-[#a0aabf] flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[10px] text-[#647087] w-32 flex-shrink-0">
                        {new Date(ev.created_at).toLocaleString('en-GB')}
                      </span>
                      <span className="text-[#647087]">{ev.from_status || "—"}</span>
                      <ArrowRight className="w-3 h-3 text-[#1fb6c1]" />
                      <span className="text-white">{ev.to_status}</span>
                      <span className="ml-auto text-[#A78BFA]">by {ev.by_user_name || "—"}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="hv-card p-4" data-testid="task-notes-section">
              <div className="text-xs uppercase tracking-wider text-[#647087] mb-2">Notes & handover history</div>
              <div className="space-y-2">
                {(task.notes || []).length === 0 && (
                  <div className="text-sm text-[#647087]">No notes yet.</div>
                )}
                {(task.notes || []).map((n) => (
                  <div key={n.id} className="border border-white/5 rounded-md p-2.5 text-sm bg-white/[0.02]">
                    <div className="text-xs text-[#647087]">
                      <span className="text-[#A78BFA]">{n.by_user_name}</span> ·{" "}
                      {new Date(n.created_at).toLocaleString('en-GB')}
                      {n.transferred_to_name && (
                        <span className="ml-1">
                          → <span className="text-[#1fb6c1]">{n.transferred_to_name}</span>
                        </span>
                      )}
                    </div>
                    {n.text && <div className="mt-1 whitespace-pre-wrap">{n.text}</div>}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <input
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Add a note…"
                  className="hv-input flex-1 py-1.5 text-sm"
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addNote())}
                  data-testid="task-note-input"
                />
                <button
                  onClick={addNote}
                  className="hv-btn hv-btn-primary px-3"
                  data-testid="task-note-add"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <HistoryBlock entity={{ id, kind: "task" }} />
          </>
        )}

        {tab === "transfer" && (
          <div className="hv-card p-4" data-testid="task-transfer-section">
            <div className="text-xs uppercase tracking-wider text-[#647087] mb-2 flex items-center gap-1">
              <ArrowRight className="w-3 h-3" /> Transfer to next user
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              <select
                value={transferTo}
                onChange={(e) => setTransferTo(e.target.value)}
                className="hv-input py-1.5 text-sm"
                data-testid="task-transfer-select"
              >
                <option value="">Select user…</option>
                {users
                  .filter((u) => !task.assigned_to_ids?.includes(u.id))
                  .map((u) => (
                    <option key={u.id} value={u.id}>{u.name || u.email}</option>
                  ))}
              </select>
              <input
                value={transferNote}
                onChange={(e) => setTransferNote(e.target.value)}
                placeholder="Handover note (e.g. 'Done my part — pls invoice')"
                className="hv-input py-1.5 text-sm"
                data-testid="task-transfer-note"
              />
            </div>
            <button
              onClick={transfer}
              disabled={!transferTo}
              className="hv-btn hv-btn-primary mt-2 disabled:opacity-50"
              data-testid="task-transfer-btn"
            >
              <ArrowRight className="w-3 h-3" /> Transfer
            </button>
          </div>
        )}
      </div>
    </EntityShell>
  );
}
